import React, { memo, useEffect, useRef } from 'react';
import MonacoEditor, { loader } from '@monaco-editor/react';
import * as monacoEditor from 'monaco-editor';
import * as monaco from './monaco';
import css from './styles.scss';
import { findLineNumbersToHighlightYaml } from './utils';

export interface Props {
  policyYaml: string;
  yamlToHighlight?: string | null;
}

const LINE_HEIGHT = 24;

loader.config({ monaco: monacoEditor });

const EDITOR_OPTIONS: monaco.editor.IStandaloneEditorConstructionOptions = {
  readOnly: true,
  fontSize: 16,
  fontFamily: 'Roboto Mono, monospace',
  lineHeight: LINE_HEIGHT,
  guides: { indentation: false },
  minimap: {
    enabled: false,
  },
  tabSize: 2,
  wordBasedSuggestions: false,
  renderLineHighlight: 'none',
  cursorBlinking: 'solid',
};

export const YAMLEditor = memo<Props>(function YAMLEditor(props) {
  const ref = useRef<monaco.editor.IStandaloneCodeEditor>();

  const onEditorMount = (editor: monaco.editor.IStandaloneCodeEditor) => {
    ref.current = editor;
  };

  useEffect(() => {
    const node = ref.current?.getDomNode()?.parentNode as
      | HTMLElement
      | undefined;
    if (!node || !('ResizeObserver' in window)) return;
    const observer = new window.ResizeObserver(() => {
      requestAnimationFrame(() => ref.current?.layout());
    });
    observer.observe(node);
    return () => observer.unobserve(node);
  }, [ref.current]);

  useEffect(() => {
    const editor = ref.current;
    if (!editor) return;

    if (!props.yamlToHighlight) return;

    const ranges = findLineNumbersToHighlightYaml(
      props.policyYaml,
      props.yamlToHighlight,
      new Set(['spec', 'specs', 'ingress', 'egress', '- ingress', '- egress']),
    ).map(lineNumber => ({
      range: new monaco.Range(lineNumber + 1, 0, lineNumber + 1, 0),
      options: { isWholeLine: true, className: css.ruleInlineHightlight },
    }));

    const decorations = editor.deltaDecorations([], ranges);

    editor.setSelection(new monaco.Selection(1, 1, 1, 1));
    if (ranges.length) {
      const endLineNumber = ranges[ranges.length - 1].range.endLineNumber;
      const endLineScroll = endLineNumber * LINE_HEIGHT;

      const editorHeight =
        editor.getDomNode()?.clientHeight ?? editor.getContentHeight();

      const scroll = Math.max(
        0,
        Math.min(
          editor.getScrollHeight(),
          endLineScroll - editorHeight + LINE_HEIGHT,
        ),
      );

      editor.setScrollTop(scroll);
    } else {
      editor.setScrollTop(0);
    }

    return () => {
      if (!decorations) return;
      editor.deltaDecorations(decorations, []);
    };
  }, [ref.current, props.policyYaml, props.yamlToHighlight]);

  return (
    <MonacoEditor
      value={props.policyYaml}
      language="yaml"
      options={EDITOR_OPTIONS}
      onMount={onEditorMount}
    />
  );
});

export default YAMLEditor;
